import React, { createContext, useContext, useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { buildBundles } from './pressingHelpers';
import SmartProductImage from './SmartProductImage';
import NumberPreviewThumb, { buildFontFaceCss } from './NumberPreviewThumb';
import { getPreviewImageUrls, getPreviewFrontUrl, shopifyResize } from './previewImage';
import { compareBins, isWarehouseLocation } from './binSort';

// ─── Public exports preserved for existing callers ──────────────────────────
export const NEW_PRINT_TAG = 'OpsHubPrinted';
export const PRINT_TAGS = new Set(['opshubprinted', 'order printed', 'osp: order printed']);
export const isPrinted = (tags) => (tags || []).some(t => PRINT_TAGS.has(t.toLowerCase()));

// Returns an array of location-suffix strings for per-location print tags.
// e.g. "OpsHubPrinted Teams Lancaster" → "Teams Lancaster"
export const getPerLocationPrintTags = (tags) =>
    (tags || [])
        .filter(t => /^OpsHubPrinted\s+\S/i.test(t))
        .map(t => t.replace(/^OpsHubPrinted\s+/i, ''));

// Returns true if ANY bare/legacy print tag is present (not the per-location ones).
// Legacy tags: "OpsHubPrinted" (no suffix), "order printed", "osp: order printed".
export const hasLegacyPrintTag = (tags) =>
    (tags || []).some(t => {
        const lc = (t || '').toLowerCase().trim();
        return lc === 'opshubprinted' || lc === 'order printed' || lc === 'osp: order printed';
    });

const PICKUP_TYPES = new Set(['LOCAL_PICKUP', 'PICK_UP', 'RETAIL']);
const PRINT_HIDDEN_KEYS = new Set(['preview front', 'preview back', 'preview', 'just_preview', 'parent product']);
const EMBELLISHMENT_TYPES = new Set(['Logo', 'LOGO', 'Name', 'Number', 'Embroidered Logo', 'Embroidered Number', 'Crest', 'Decals']);
// Service / protection line items — they sit at the bottom as compact one-liners.
const VIRTUAL_TITLES = new Set([
    'free returns + package protection',
    'processing fee',
    'tip',
    'shipping',
    'tax',
    'gift card',
]);
// Per-order {sku: notification_message} map. Provided by OrderPrintCard so
// LineItem / BundleView / VirtualRow can render a small red badge under any
// SKU that has an active SKU notification without prop-drilling through
// every wrapper. Empty by default → renders nothing.
const SkuNotifContext = createContext({});

function SkuNotifBadge({ sku, size = 'md' }) {
    const map = useContext(SkuNotifContext);
    if (!sku) return null;
    const msg = map[sku];
    if (!msg) return null;
    const s = size === 'sm'
        ? { fontSize: 8.5,  padding: '2px 6px', marginTop: 2 }
        : { fontSize: 10,   padding: '3px 8px', marginTop: 3 };
    return (
        <div className="sp-sku-notif" style={{
            display: 'inline-block',
            marginTop: s.marginTop,
            padding: s.padding,
            borderRadius: 10,
            background: '#fde8e8',
            color: '#c00',
            fontWeight: 700,
            fontSize: s.fontSize,
            border: '1px solid #fbb',
            lineHeight: 1.15,
        }}>{msg}</div>
    );
}

function isVirtualItem(item) {
    const t = (item?.title || '').trim().toLowerCase();
    if (!t) return false;
    if (VIRTUAL_TITLES.has(t)) return true;
    return /^(tip|gift card|shipping|tax|service fee)/i.test(t);
}

// Kept for backwards-compat with any consumer that still references this set.
export const JUMBLE_COLORS = [
    { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
    { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
    { bg: '#fce7f3', text: '#9d174d', border: '#ec4899' },
    { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
    { bg: '#ede9fe', text: '#4c1d95', border: '#8b5cf6' },
    { bg: '#ffedd5', text: '#9a3412', border: '#f97316' },
];

export function groupLineItems(lineItems, tags) {
    return mergeQtySplits(buildBundles(lineItems, { tags }).map(b => ({ item: b.parent, children: b.children })));
}

// 2026-09-28 (Eric, SP-373105): Shopify split one purchase of 16 cold
// compresses into two line items of the same SKU (11 + 5, different unit
// prices). The picker needs ONE row saying 16, not two rows or, worse, a
// child row with no quantity. Standalone rows (no bundle children) that share
// the variant / SKU and carry identical attributes are folded into one row
// with the quantities summed. Lines with differing attributes (two players'
// pants) stay separate.
export function mergeQtySplits(bundles) {
    const out = [];
    const byKey = new Map();
    const attrKey = (li) => JSON.stringify((li?.customAttributes || []).map(a => [a.key, a.value]).sort());
    for (const b of bundles) {
        const li = b.item;
        const sku = (li?.sku || '').trim();
        if (b.children.length || !li || !sku) { out.push(b); continue; }
        const key = `${li?.variant?.id || sku}|${sku}|${attrKey(li)}`;
        const prev = byKey.get(key);
        if (prev) {
            const merged = { ...prev.item, quantity: (Number(prev.item.quantity) || 0) + (Number(li.quantity) || 0),
                             _mergedLineItemIds: [...(prev.item._mergedLineItemIds || [prev.item.id]), li.id] };
            prev.item = merged;
            continue;
        }
        const copy = { item: li, children: [] };
        byKey.set(key, copy);
        out.push(copy);
    }
    return out;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

const moneySet = (set) => set?.shopMoney
    ? parseFloat(set.shopMoney.amount).toFixed(2)
    : null;

function getAttr(attrs, ...keys) {
    if (!attrs) return null;
    const list = attrs || [];
    for (const k of keys) {
        const kLower = k.toLowerCase();
        const f = list.find(a => a.key === k || a.key.toLowerCase() === kLower);
        if (f?.value) return f.value;
    }
    return null;
}

function nonHiddenAttrs(item) {
    return (item?.customAttributes || []).filter(a =>
        !a.key.startsWith('_') && !PRINT_HIDDEN_KEYS.has(a.key.toLowerCase())
    );
}

function buildPlayerGroups(bundles) {
    // Key by PLAYER NAME only. A single player's items may have different
    // Player Number attributes across bundles (some bundles have #8, others
    // have no number because the bundle is number-less) — those must still
    // collapse into ONE group. Number/team/role from any bundle wins.
    const byKey = new Map();
    const order = [];
    for (const b of bundles) {
        const parent = b.item;
        const allAttrs = [
            ...(parent?.lineItemGroup?.customAttributes || []),
            ...(parent?.customAttributes || []),
            ...(b.children || []).flatMap(c => c.customAttributes || []),
        ];
        const name   = getAttr(allAttrs, 'Player Name', 'player_name');
        const number = getAttr(allAttrs, 'Player Number', 'player_number', 'Number', '#');
        const team   = getAttr(allAttrs, 'Team', 'team');
        const role   = getAttr(allAttrs, 'Role', 'role');
        const key = name ? `name:${name}` : '__none__';
        if (!byKey.has(key)) {
            const g = { key, name, number, team, role, initials: [], bundles: [] };
            byKey.set(key, g);
            order.push(g);
        }
        const g = byKey.get(key);
        // First non-null wins for number/team/role (lets any bundle carry it in).
        if (!g.number && number) g.number = number;
        if (!g.team   && team)   g.team   = team;
        if (!g.role   && role)   g.role   = role;
        // Initials / text-to-apply lives in the line item's `Name` attribute on
        // customization products (e.g. the silicone-initials line item carries
        // Name=SKA). Walk parent + children per-line so we catch both bundled
        // children and standalone initials items. Skip values that just echo
        // the player's own name — that's redundant noise on the print.
        const playerLc = (name || '').trim().toLowerCase();
        for (const li of [parent, ...(b.children || [])]) {
            if (!li) continue;
            const lbl = (getAttr(li.customAttributes || [], 'Name') || '').trim();
            if (lbl && lbl.toLowerCase() !== playerLc
                && !g.initials.some(x => x.toLowerCase() === lbl.toLowerCase())) {
                g.initials.push(lbl);
            }
        }
        g.bundles.push(b);
    }
    return order;
}

function resolveParent(bundle) {
    const all = [bundle.item, ...(bundle.children || [])];
    return all.find(li => !EMBELLISHMENT_TYPES.has(li?.productType)) || bundle.item;
}

function resolveChildren(bundle, parent) {
    const all = [bundle.item, ...(bundle.children || [])];
    return all.filter(li => li && li.id !== parent.id);
}

// ─── SPBarcode ──────────────────────────────────────────────────────────────

function SPBarcode({ value, height = 40, width = 1.5, displayValue = true, fontSize = 10 }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current || !value) return;
        try {
            JsBarcode(ref.current, value, {
                format: 'CODE128', width, height, displayValue,
                fontSize, margin: 0, background: '#ffffff', lineColor: '#111418',
                font: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            });
        } catch (_) { /* unsupported chars → empty svg */ }
    }, [value, height, width, displayValue, fontSize]);
    return <svg ref={ref} style={{ display: 'block' }} />;
}

// ─── DocHeader ──────────────────────────────────────────────────────────────

// Logo source: per-store PNG from /api/store-logos/{sk}.png, falling back to
// the bundled soccer-post-logo.png if the per-store file 404s and finally to
// a styled plain-text store name. Bootstrap at backend startup pulls every
// store's logo from Shopify checkout branding, so most stores have a real
// asset; manual upload via Settings → Locations overrides.
function DocHeader({ orderName, orderDate, club, location, storeKey, storeDisplayName, page = 1, totalPages = 1, originalName = null }) {
    const sk = storeKey || 'soccerpost';
    const logoUrl = `/api/store-logos/${sk}.png`;
    // onError chain: per-store → null → swap to text fallback by hiding the img.
    const onImgError = (e) => {
        // Hide the broken image and show the text fallback sibling
        e.currentTarget.style.display = 'none';
        const fallback = e.currentTarget.nextElementSibling;
        if (fallback) fallback.style.display = 'block';
    };
    return (
        <header className="sp-doc-header">
            <div className="sp-doc-header__left">
                <img
                    src={logoUrl}
                    alt={storeDisplayName || 'Store logo'}
                    className="sp-doc-header__logo"
                    onError={onImgError}
                />
                <div
                    className="sp-doc-header__logo-text"
                    style={{ display: 'none', fontWeight: 700, fontSize: '24px', letterSpacing: '0.5px' }}
                >
                    {storeDisplayName || sk}
                </div>
            </div>
            <div className="sp-doc-header__mid">
                {/* Replacement orders stack the crossed-out raw name ABOVE a
                    slightly smaller hero so the longer alias (SP-355545:A)
                    never clips against the barcode slot. */}
                <div className="sp-doc-header__title-row"
                     style={originalName ? { flexDirection: 'column', alignItems: 'center', gap: 2 } : undefined}>
                    {originalName && (
                        <span style={{ textDecoration: 'line-through', opacity: 0.55, fontSize: 13 }}>
                            {originalName}
                        </span>
                    )}
                    <span className="sp-h-hero"
                          style={originalName ? { color: '#c1281c', fontSize: 24, whiteSpace: 'nowrap' } : undefined}>
                        {orderName}
                    </span>
                </div>
                <dl className="sp-doc-header__meta">
                    {/* Club and Page removed from the top meta — Club lives
                        in the Order column, and Page N of N now renders in
                        the @page top-right margin on every page (including
                        page 1) via POPUP_SCRIPT. */}
                    <div><dt>Date</dt><dd>{orderDate}</dd></div>
                    {location && <div><dt>Location</dt><dd>{location}</dd></div>}
                </dl>
            </div>
            <div className="sp-doc-header__right">
                {/* 2026-08-03: bumped bar width 1.5 → 2.15 (+43% total) —
                    scanner reliability complaint from packing team. Height +
                    fontSize unchanged so the barcode still fits the header slot. */}
                <SPBarcode value={orderName} height={46} width={2.15} fontSize={10} />
            </div>
        </header>
    );
}

// ─── PartyStrip ─────────────────────────────────────────────────────────────

// Order Notes on the printout (2026-10-02, Eric): B2B orders print the order's note at the top,
// between "Ship to" and "Method", so whoever picks and packs the order reads it first. Shown
// only when the order has a note. Boxed so it stands out on paper; the whole note prints.
function PrintOrderNotes({ note, style }) {
    return (
        <div className="sp-party__col sp-party__notes" style={{ marginLeft: 0, ...style }}>
            <div style={{ border: '1.5px solid #000', borderRadius: 4, padding: '6px 9px', pageBreakInside: 'avoid' }}>
                <div className="sp-h-section" style={{ marginBottom: 3 }}>Order Notes</div>
                <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, color: 'var(--sp-ink)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{note}</div>
            </div>
        </div>
    );
}

function PartyStrip({ club, players = [], shipTo, deliveryMethod, pickupStoreName, fulfillBy, shippingLine, isWarehouse = false, orderNote = null }) {
    // `players` shape now: [{name, number, team}] — each player paired with
    // their own team so the stack reads player → team → player → team
    // rather than all players above all teams.
    const isPickup = !shipTo || /pickup|retail|local_pickup/i.test(deliveryMethod || '');
    // 2026-07-07: Warehouse-side prints drop the Club/Players column
    // (no personalization work happens at the warehouse — the slip only
    // needs Ship to + Method) and expand the remaining two columns to
    // 50/50 for readability. `sp-party--2col` inline-styles the grid so
    // the change works without a CSS-file update.
    // With a note the warehouse strip becomes Ship to | Order Notes | Method (the screenshot Eric
    // marked up); on the three-column (club) layout the note goes on its own row under the strip.
    const note = String(orderNote || '').trim();
    const noteInStrip = !!note && isWarehouse;
    return (
        <>
        <section
            className={`sp-party${isWarehouse ? ' sp-party--2col' : ''}`}
            style={isWarehouse
                ? { display: 'grid', gridTemplateColumns: noteInStrip ? '0.7fr 1.1fr 1fr' : '1fr 1fr', gap: 24 }
                : undefined}
        >
            {/* Left column replaces the old "Bill to": Club + Players (with
                their numbers) + Team names. "ORDER" label removed per user
                spec to free up vertical space. */}
            {!isWarehouse && (
                <div className="sp-party__col">
                    {club && <div className="sp-party__name">{club}</div>}
                    {players.length > 0 && (
                        <div className="sp-party__players">
                            {players.map((p, i) => (
                                <div key={i} className="sp-party__player-row">
                                    <div className="sp-party__player">
                                        <span className="sp-party__player-name">{p.name}</span>
                                        {p.number != null && p.number !== '' && (
                                            <span className="sp-badge-num">#{p.number}</span>
                                        )}
                                        {(p.initials || []).map((v, j) => (
                                            <span key={`pini-${j}`} className="sp-badge-ini">{v}</span>
                                        ))}
                                    </div>
                                    {p.team && <div className="sp-party__player-team">{p.team}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            <div className="sp-party__col">
                <div className="sp-h-section">{isPickup ? 'Pickup' : 'Ship to'}</div>
                {isPickup ? (
                    <>
                        <div className="sp-party__name">{pickupStoreName || 'In-store pickup'}</div>
                        <div className="sp-p-muted">{pickupStoreName ? 'Customer will collect at this store' : 'Customer will collect at store'}</div>
                    </>
                ) : shipTo ? (
                    <>
                        <div className="sp-party__name">{shipTo.name}</div>
                        {shipTo.company && <div className="sp-p">{shipTo.company}</div>}
                        {shipTo.line1 && <div className="sp-p">{shipTo.line1}</div>}
                        {shipTo.line2 && <div className="sp-p">{shipTo.line2}</div>}
                        {(shipTo.city || shipTo.state || shipTo.zip) && (
                            <div className="sp-p">
                                {[shipTo.city, shipTo.state].filter(Boolean).join(', ')}{shipTo.zip ? ` ${shipTo.zip}` : ''}
                            </div>
                        )}
                        {shipTo.phone && <div className="sp-p-muted">{shipTo.phone}</div>}
                    </>
                ) : <div className="sp-p-muted">—</div>}
            </div>
            {noteInStrip && <PrintOrderNotes note={note} />}
            <div className="sp-party__col">
                <div className="sp-h-section">Method</div>
                <div className="sp-party__name">{shippingLine || deliveryMethod || '—'}</div>
                {fulfillBy && (
                    <div className="sp-p-muted">
                        Fulfill by <strong style={{ color: 'var(--sp-ink)' }}>{fulfillBy}</strong>
                    </div>
                )}
            </div>
        </section>
        {note && !noteInStrip && <PrintOrderNotes note={note} style={{ margin: '10px 0 0' }} />}
        </>
    );
}

// ─── PlayerGroup ────────────────────────────────────────────────────────────

// Defensive coerce — team/role values may be objects ({id, title}) coming from
// Team Portal JSON blobs. Rendering an object as a React child throws error #31.
const asText = (v) => {
    if (v == null) return null;
    if (typeof v === 'string' || typeof v === 'number') return String(v);
    if (typeof v === 'object') return v.title || v.name || null;
    return null;
};
function PlayerGroup({ name, number, team, role, initials = [], children }) {
    const teamTxt = asText(team);
    const roleTxt = asText(role);
    if (!name && !number) return <>{children}</>;
    return (
        <section className="sp-player-group">
            <header className="sp-player-group__bar">
                <span className="sp-player-group__name">{asText(name) || '—'}</span>
                {number != null && number !== '' && <span className="sp-badge-num">#{asText(number)}</span>}
                {initials.map((v, i) => (
                    <span key={`ini-${i}`} className="sp-badge-ini">{v}</span>
                ))}
                {teamTxt && <span className="sp-badge-team">{teamTxt}</span>}
                {roleTxt && <span className="sp-player-group__meta sp-player-group__meta--faint">{roleTxt}</span>}
            </header>
            {children}
        </section>
    );
}

// ─── LineItem ───────────────────────────────────────────────────────────────

function LineItem({ item, bin, overstock = null, showBin = true, showPrice = true, hasBinColumn = true }) {
    const variant = item?.variant?.title && item.variant.title !== 'Default Title' ? item.variant.title : '';
    const priceAmt = item?.discountedUnitPriceSet?.shopMoney?.amount
        ?? item?.originalUnitPriceSet?.shopMoney?.amount
        ?? item?.variant?.price
        ?? item?.price;
    const returnableStr = getAttr(item?.customAttributes || [], 'Returnable');
    const returnableLabel = returnableStr == null
        ? null
        : (/^(no|false)$/i.test(returnableStr.trim())
            ? 'Not returnable'
            : (/^(yes|true)$/i.test(returnableStr.trim())
                ? 'Returnable'
                : returnableStr));
    // Attrs in the body — skip Returnable (rendered as pill below), skip
    // Player-info keys (handled by the player-group bar).
    const attrs = nonHiddenAttrs(item).filter(a => {
        const k = a.key.toLowerCase();
        return k !== 'returnable'
            && k !== 'player name' && k !== 'player_name'
            && k !== 'player number' && k !== 'player_number'
            && k !== 'number' && k !== '#'
            && k !== 'team' && k !== 'role';
    });
    // Prefer a customizer-rendered preview (Soccer Post Team Portal /
    // PPLR / etc.) over the raw Shopify product image when one is configured
    // — operators picking/pressing/packing expect to see what's actually
    // printed on the garment, not the blank product photo.
    const itemPreview = getPreviewFrontUrl(item?.customAttributes || []);
    const linePreviewSrc = itemPreview || item?.image?.url;
    return (
        <div className="sp-line">
            {/* Single-product preview: centered in the 130px cell. */}
            <figure className="sp-line__thumb-fig sp-line__thumb-fig--single">
                <div className="sp-line__thumb">
                    {/* SmartProductImage paints a grey background when it
                        detects a white-on-white product (via its onLoad
                        canvas analysis), matching the order-page behavior. */}
                    {linePreviewSrc
                        ? <SmartProductImage src={linePreviewSrc} alt="" size={400} />
                        : <div className="sp-line__thumb-fallback" aria-hidden>▢</div>}
                </div>
            </figure>
            <div className="sp-line__body">
                <div className="sp-line__title">{item?.title || '—'}</div>
                <div className="sp-line__attrs">
                    {item?.sku && (
                        <span className="sp-line__attr sp-code">
                            {item.sku}
                            <SkuNotifBadge sku={item.sku} size="sm" />
                        </span>
                    )}
                    {attrs.map(a => (
                        <span key={a.key} className="sp-line__attr">
                            <span className="sp-line__attr-k">{a.key}:</span> {a.value}
                        </span>
                    ))}
                </div>
                {returnableLabel && (
                    <div className="sp-line__returnable">
                        <span className="sp-pill">{returnableLabel}</span>
                    </div>
                )}
            </div>
            {hasBinColumn && (
                <div className="sp-line__bin">
                    {showBin && bin ? <span className="sp-highlight sp-code">{bin}</span> : null}
                    {showBin && overstock ? (
                        <div className="sp-line__overstock"><span className="sp-code">{overstock}</span></div>
                    ) : null}
                </div>
            )}
            <div className="sp-line__variant sp-p-muted">{variant || ''}</div>
            <div className="sp-line__qty sp-tnum">{item?.quantity ?? ''}</div>
            <div className="sp-line__price sp-tnum">
                {showPrice && priceAmt != null ? `$${parseFloat(priceAmt).toFixed(2)}` : ''}
            </div>
        </div>
    );
}

// ─── BundleItem ─────────────────────────────────────────────────────────────

function BundleItem({ parent, children = [], bins = {}, overstockBins = {}, showBin = true, showPrice = true, hasBinColumn = true, numberPreviews = {}, printedNumber: inheritedNumber = null }) {
    const allAttrs = [
        ...(parent?.lineItemGroup?.customAttributes || []),
        ...(parent?.customAttributes || []),
        // Children carry preview attrs for some 3rd-party customizers (PPLR
        // attaches the marker + URL to the embellishment line item, not the
        // garment), so include them when resolving the bundle preview.
        ...(children || []).flatMap(c => c?.customAttributes || []),
    ];
    const { front: previewFront, back: previewBack } = getPreviewImageUrls(allAttrs);
    const variant = parent?.variant?.title && parent.variant.title !== 'Default Title' ? parent.variant.title : '';
    const priceAmt = parent?.discountedUnitPriceSet?.shopMoney?.amount
        ?? parent?.originalUnitPriceSet?.shopMoney?.amount
        ?? parent?.variant?.price
        ?? parent?.price;
    const returnableStr = getAttr(parent?.customAttributes || [], 'Returnable');
    // One pill style for every variant (Returnable / Not returnable /
    // Exchange Only / Exchangeable / …). We just display the raw attribute
    // value and let the single neutral pill style handle it.
    const returnableLabel = returnableStr == null
        ? null
        : (/^(no|false)$/i.test(returnableStr.trim())
            ? 'Not returnable'
            : (/^(yes|true)$/i.test(returnableStr.trim())
                ? 'Returnable'
                : returnableStr));
    // Per-bundle customization attributes: Name = text printed on the
    // garment (e.g. a surname or initials), Number = jersey number. These
    // are bundle-specific so we show them even though the player bar
    // already carries the CUSTOMER'S Player Name / Player Number — these
    // are the PRINTED values, which may differ.
    //
    // The attribute can live in 3 places depending on how the bundle was
    // configured: the parent line item, the parent's lineItemGroup, or any
    // embellishment child (e.g. a Stahls Initials line item carrying Name=SD
    // for an attached hoodie/top). Walk all three so we don't lose the
    // attribute when only the Initials child is configured with it.
    const bundleAttrs = [
        ...(parent?.customAttributes || []),
        ...(parent?.lineItemGroup?.customAttributes || []),
        ...(children || []).flatMap(c => c?.customAttributes || []),
    ];
    const printedName   = getAttr(bundleAttrs, 'Name');
    // inheritedNumber (from playerGroup) wins when set — it carries any RDS
    // Team Portal number override applied at the order level. Fallback is the
    // raw checkout attribute embedded on the bundle line items.
    const printedNumber = inheritedNumber || getAttr(bundleAttrs, 'Number');
    return (
        <div className="sp-bundle">
            <div className={`sp-bundle__preview${previewBack ? '' : ' sp-bundle__preview--single'}`}>
                {previewFront && (
                    <figure className="sp-bundle__preview-fig">
                        <div className="sp-bundle__preview-img">
                            <img src={shopifyResize(previewFront, 400)} alt="Front" />
                        </div>
                        {previewBack && <figcaption>Front</figcaption>}
                    </figure>
                )}
                {previewBack && (
                    <figure className="sp-bundle__preview-fig">
                        <div className="sp-bundle__preview-img">
                            <img src={shopifyResize(previewBack, 400)} alt="Back" />
                        </div>
                        <figcaption>Back</figcaption>
                    </figure>
                )}
                {!previewFront && !previewBack && (
                    <figure className="sp-bundle__preview-fig">
                        <div className="sp-bundle__preview-img">
                            {parent?.image?.url
                                ? <SmartProductImage src={parent.image.url} alt="" size={400} />
                                : <span className="sp-bundle__preview-img--empty">▢</span>}
                        </div>
                    </figure>
                )}
            </div>

            <div className="sp-bundle__body">
                <div className="sp-bundle__title-row">
                    <span className="sp-bundle__title">{parent?.title || '—'}</span>
                </div>
                {parent?.sku && (
                    <div className="sp-bundle__sku-line sp-code">
                        {parent.sku}
                        <SkuNotifBadge sku={parent.sku} />
                    </div>
                )}
                {(printedName || printedNumber) && (
                    <div className="sp-bundle__attrs">
                        {printedName && (
                            <span className="sp-bundle__attr">
                                <span className="sp-bundle__attr-k">Name</span>
                                <span className="sp-bundle__attr-v">{printedName}</span>
                            </span>
                        )}
                        {printedNumber && (
                            <span className="sp-bundle__attr">
                                <span className="sp-bundle__attr-k">Number</span>
                                <span className="sp-bundle__attr-v">{printedNumber}</span>
                            </span>
                        )}
                    </div>
                )}
                {returnableLabel && (
                    <div className="sp-bundle__returnable">
                        <span className="sp-pill">{returnableLabel}</span>
                    </div>
                )}
            </div>

            {hasBinColumn && (
                <div className="sp-bundle__bin">
                    {showBin && bins[parent?.id] ? <span className="sp-highlight sp-code">{bins[parent.id]}</span> : null}
                    {showBin && overstockBins[parent?.id] ? (
                        <div className="sp-bundle__overstock"><span className="sp-code">{overstockBins[parent.id]}</span></div>
                    ) : null}
                </div>
            )}
            <div className="sp-bundle__variant sp-p-muted">{variant || ''}</div>
            <div className="sp-bundle__qty sp-tnum">{parent?.quantity ?? ''}</div>
            <div className="sp-bundle__price sp-tnum">
                {showPrice && priceAmt != null ? `$${parseFloat(priceAmt).toFixed(2)}` : ''}
            </div>

            {children.length > 0 && (
                <div className="sp-bundle__subs">
                    {children.map((c) => {
                        const childPid = c?.variant?.product?.id;
                        const preview = childPid ? numberPreviews[childPid] : null;
                        // Number to render: prefer the child's own Number
                        // attr (each embellishment line item carries it),
                        // fall back to the bundle's printedNumber, then
                        // fall back to the metaobject's default_value.
                        const childNumber = getAttr(c?.customAttributes || [], 'Number');
                        // inheritedNumber (RDS Team Portal override) wins over
                        // the child's own checkout-embedded Number attr.
                        const numberToRender = inheritedNumber
                            || childNumber
                            || printedNumber
                            || preview?.defaultValue
                            || '';
                        const useNumberPreview = preview && numberToRender;
                        return (
                        <div key={c.id} className="sp-bundle__sub">
                            <div className="sp-bundle__sub-thumb">
                                {useNumberPreview
                                    ? <NumberPreviewThumb preview={preview} number={numberToRender} size={48} />
                                    : (c?.image?.url
                                        ? <SmartProductImage src={c.image.url} alt="" size={200} />
                                        : <span className="sp-bundle__sub-thumb-label">▢</span>)}
                            </div>
                            <div className="sp-bundle__sub-body">
                                <div className="sp-bundle__sub-title">{c.title}</div>
                                {c.sku && (
                                    <div className="sp-bundle__sub-sku sp-code">
                                        {c.sku}
                                        <SkuNotifBadge sku={c.sku} size="sm" />
                                    </div>
                                )}
                                {showBin && bins[c.id] && (
                                    <div className="sp-bundle__sub-bin">
                                        <span className="sp-highlight sp-code">{bins[c.id]}</span>
                                    </div>
                                )}
                                {showBin && overstockBins[c.id] && (
                                    <div className="sp-bundle__sub-overstock">
                                        <span className="sp-code">{overstockBins[c.id]}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Totals ─────────────────────────────────────────────────────────────────

function Totals({ subtotal, shipping, shippingLabel, tax, discount, total }) {
    const row = (label, value, opts = {}) => (
        <div className={`sp-totals__row${opts.grand ? ' sp-totals__row--grand' : ''}`} key={label}>
            <span className="sp-totals__label">{label}</span>
            <span className="sp-totals__value sp-tnum">{value}</span>
        </div>
    );
    const has = (v) => v != null && v !== '';
    return (
        <div className="sp-totals">
            {has(subtotal) && row('Subtotal', `$${Number(subtotal).toFixed(2)}`)}
            {has(discount) && Number(discount) > 0 && row('Discount', `−$${Number(discount).toFixed(2)}`)}
            {has(shipping) && row(`Shipping${shippingLabel ? ` · ${shippingLabel}` : ''}`, `$${Number(shipping).toFixed(2)}`)}
            {has(tax) && row('Tax', `$${Number(tax).toFixed(2)}`)}
            {has(total) && row('Total', `$${Number(total).toFixed(2)}`, { grand: true })}
        </div>
    );
}

// ─── OrderPrintCard (default export) ────────────────────────────────────────

// Default footer used when the caller hasn't fetched the per-store override
// yet. Matches the pre-2026-06-14 hardcoded soccerpost copy.
const DEFAULT_FOOTER_HTML = (
    '<div class="sp-doc-footer__title">Thank you for choosing Soccer Post</div>' +
    '<div class="sp-doc-footer__sub">We hope you love your new gear. Play Local. Shop Local.</div>'
);

// Allowlist that mirrors backend _sanitize_footer_html. Defense in depth so
// a future DB-write bypass or backend-sanitizer regression still can't slip
// active content into the print popup.
const FOOTER_ALLOWED_TAGS = new Set([
    'div', 'p', 'span', 'br', 'strong', 'em', 'b', 'i', 'u', 'small',
    'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
]);
const FOOTER_ALLOWED_ATTRS_GLOBAL = new Set(['class', 'style']);
const FOOTER_ALLOWED_ATTRS_A      = new Set(['href', 'target', 'rel']);

function sanitizeFooterHtml(html) {
    if (!html) return '';
    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return '';
    let doc;
    try { doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html'); }
    catch (_) { return ''; }
    const root = doc.getElementById('root');
    if (!root) return '';
    const walk = (node) => {
        const kids = Array.from(node.childNodes);
        for (const child of kids) {
            if (child.nodeType === Node.ELEMENT_NODE) {
                const tag = child.tagName.toLowerCase();
                if (!FOOTER_ALLOWED_TAGS.has(tag)) {
                    // Unwrap — replace the disallowed element with its children
                    while (child.firstChild) node.insertBefore(child.firstChild, child);
                    node.removeChild(child);
                    continue;
                }
                for (const attr of Array.from(child.attributes)) {
                    const name = attr.name.toLowerCase();
                    const val  = attr.value || '';
                    const allowed =
                        FOOTER_ALLOWED_ATTRS_GLOBAL.has(name) ||
                        (tag === 'a' && FOOTER_ALLOWED_ATTRS_A.has(name));
                    if (!allowed) { child.removeAttribute(attr.name); continue; }
                    // Block javascript:/data: URIs on href
                    if (name === 'href' && /^\s*(javascript|data):/i.test(val)) {
                        child.removeAttribute(attr.name);
                    }
                    // Block javascript: anywhere in inline style
                    if (name === 'style' && /javascript\s*:/i.test(val)) {
                        child.removeAttribute(attr.name);
                    }
                }
                walk(child);
            }
            // Text/comment nodes: leave text alone (DOMParser handles entity
            // safety); strip comments which can sometimes carry conditional
            // IE-style code in old-browser corners.
            else if (child.nodeType === Node.COMMENT_NODE) {
                node.removeChild(child);
            }
        }
    };
    walk(root);
    return root.innerHTML;
}

// 2026-09-26 (Eric): `hideHeader` / `blockLabel` let a merged NJ-warehouses ticket
// stack two location blocks under one order header — see MergedNjPrint below.
export function OrderPrintCard({ fo, fullOrder, bins = {}, overstockBins = {}, numberPreviews = {}, locationId = null, storeKey = 'soccerpost', storeDisplayName = 'Soccer Post', printoutFooterHtml = null, skuNotifications = {}, printAll = false, hideHeader = false, blockLabel = null }) {
    const o   = fo?.order || fo || {};
    const ord = fullOrder || {};

    // TP replacement order: print the internal alias (SP-355545:A) with the
    // original crossed out; the barcode carries the alias so scanning the
    // printout opens the latest order in every portal.
    const replacementAlias    = ord.replacement_alias || o.replacement_alias || null;
    // Crossed-out name = this order's OWN raw Shopify name (e.g. SP-355560);
    // the alias (SP-355545:A) is the display + barcode name. When TP already
    // renamed the order to the alias itself (raw == alias), skip the
    // redundant crossed-out copy (2026-08-27).
    const _rawName = ord.name || o.name || null;
    const replacementOriginal = (replacementAlias && _rawName !== replacementAlias) ? _rawName : null;
    const name      = replacementAlias || ord.name || o.name || '—';
    const createdAt = ord.processedAt || ord.createdAt || o.created_at;

    const shippingAddr = ord.shippingAddress;
    // 2026-09-29: B2B store orders ship to a company (e.g. "Soccer Post Fresno")
    // with no first/last name — the company line was dropped and the slip showed
    // a bare street (B2B-93027). Company prints under the name, or as the name.
    const _personName = shippingAddr ? [shippingAddr.firstName, shippingAddr.lastName].filter(Boolean).join(' ') : '';
    const shipTo = shippingAddr ? {
        name: _personName || shippingAddr.company || '',
        company: (_personName && shippingAddr.company) ? shippingAddr.company : '',
        line1: shippingAddr.address1,
        line2: shippingAddr.address2,
        city: shippingAddr.city,
        state: shippingAddr.provinceCode,
        zip: shippingAddr.zip,
        phone: shippingAddr.phone,
    } : null;

    const fullFOs = (ord.fulfillmentOrders?.edges || []).map(e => e.node);
    // Tools-menu pickup override (custom.store_pickup_location) wins over the
    // original delivery method everywhere in OpsHub. When set, the printout
    // treats the order as Pickup at the override location.
    const pickupOverride = (o?.store_pickup_override || ord.store_pickup_override || '').trim() || null;
    const methods = pickupOverride
        ? ['PICK_UP']
        : (fullFOs.length > 0
            ? [...new Set(fullFOs.map(f => f.deliveryMethod?.methodType).filter(Boolean))]
            : (fo?.delivery_methods || (fo?.delivery_method ? [fo.delivery_method] : [])).filter(Boolean));
    const deliveryMethodRaw = pickupOverride || fullFOs[0]?.deliveryMethod?.presentedName || methods[0] || '';
    const isPickup = pickupOverride != null || methods.some(m => PICKUP_TYPES.has(m));
    const deliveryMethodTitle = isPickup ? 'Pickup in store' : (deliveryMethodRaw || 'Standard shipping');
    // Pickup destination — override > FO presentedName > LIST cache field.
    const pickupStoreName = (
        pickupOverride
        || fullFOs.find(f => f.deliveryMethod?.presentedName && /pick|retail/i.test(f.deliveryMethod?.methodType || ''))?.deliveryMethod?.presentedName
        || o?.pickup_store_name   // already prefix-stripped by the cache layer
        || ''
    );
    // 2026-07-30: only consider OPEN/IN_PROGRESS FOs when computing
    // the min fulfillBy. CLOSED FOs are already done — including them
    // in the min-across-FOs pull picks a stale ship-by date from the
    // completed cycle when there's an OPEN FO with a later, actionable
    // date. SP-290720 canonical: CLOSED FO fulfillBy=Jul 2 (done),
    // OPEN FO fulfillBy=Jul 28 (real deadline). Print now shows Jul 28.
    // Falls back to order-level fulfill_by if no open FOs.
    const _openFOs = fullFOs.filter(f => {
        const st = (f.status || '').toUpperCase();
        return st === 'OPEN' || st === 'IN_PROGRESS';
    });
    const fulfillByIso = _openFOs.reduce((min, f) => {
        if (!f.fulfillBy) return min;
        return !min || f.fulfillBy < min ? f.fulfillBy : min;
    }, null) || fo?.fulfill_by || null;

    const shipping = moneySet(ord.totalShippingPriceSet);
    const tax      = moneySet(ord.totalTaxSet);
    // 2026-09-02 (SP-340748): the order-detail query only returns
    // currentTotalDiscountsSet (the live, post-edit figure); totalDiscountsSet
    // is never fetched, so the Discount row silently never printed.
    const discount = moneySet(ord.currentTotalDiscountsSet) ?? moneySet(ord.totalDiscountsSet);
    const total    = moneySet(ord.totalPriceSet) || (o.total?.amount ? parseFloat(o.total.amount).toFixed(2) : null);

    // 2026-10-20 (B2B-95119): a line removed by Order Editing stays on the order
    // with currentQuantity 0 (the detail query normalises quantity to it), and it
    // used to print as "0 of" that item. Gone is gone — on every path, full
    // reprint included.
    const _lineRemoved = li => { const q = li?.currentQuantity ?? li?.quantity; return q != null && Number(q) === 0; };
    const rawItems = (fullOrder
        ? (fullOrder.lineItems?.edges || []).map(e => e.node)
        : (o.line_items || [])).filter(li => !_lineRemoved(li));

    // Subtotal = gross items total (pre-discount). Mirrors Shopify admin so
    // it stays consistent when a line-item discount zeroes ord.subtotalPriceSet.
    // 2026-07-07: when the order is SPLIT across multiple ops locations (any
    // combination of Teams / Warehouse), the subtotal below is recomputed
    // from ONLY the items being printed at this loc (`inScopeItems`) and
    // shipping/tax/total are suppressed — those totals belong to the whole
    // order, not this slice. See `isSplitAcrossLocations` below `inScopeItems`.
    const _liSubtotal = (li) => {
        const ot = parseFloat(li.originalTotalSet?.shopMoney?.amount);
        if (isFinite(ot)) return ot;
        const up = parseFloat(li.originalUnitPriceSet?.shopMoney?.amount)
            ?? parseFloat(li.original_unit_price);
        const qty = parseInt(li.quantity || 0, 10) || 0;
        return (isFinite(up) ? up : 0) * qty;
    };
    const subtotalSum = rawItems.reduce((s, li) => s + _liSubtotal(li), 0);
    const subtotal = subtotalSum > 0
        ? subtotalSum.toFixed(2)
        : moneySet(ord.subtotalPriceSet);

    // Unfulfilled-only filter: include items that still have remaining qty on
    // an OPEN / IN_PROGRESS fulfillment order. If no FOs are open (order is
    // fully fulfilled) we fall through to "show all items" — that path is the
    // receipt case. Filtering before grouping so bundles don't have phantom
    // embellishments from shipped-already line items.
    //
    // When locationId is provided, ALSO restrict to FOs at that location — so
    // a multi-FO order printed from one location only includes that location's
    // line items. Without locationId we fall back to all-locations behavior.
    const unfulfilledLineItemIds = new Set();
    // 2026-08-07: also track the still-to-ship qty per line item so that a
    // partially-shipped LI (e.g. qty=2 ordered, 1 already shipped) prints
    // as qty=1 instead of the full 2. SP-282086 canonical: black shorts qty
    // 2 → shipped 1 → reprint used to still say 2. Sums remainingQuantity
    // across every OPEN/IN_PROGRESS FO the LI appears on (same scope as
    // the LI-ID set above).
    const remainingQtyByLi = new Map();
    let hasAnyOpenFO = false;
    const locIdStr = locationId != null ? String(locationId) : null;
    for (const fofE of (fullOrder?.fulfillmentOrders?.edges || [])) {
        const fullFo = fofE.node;
        if (fullFo.status !== 'OPEN' && fullFo.status !== 'IN_PROGRESS') continue;
        if (locIdStr) {
            const foLocId = (fullFo.assignedLocation?.location?.id || '').split('/').pop();
            if (String(foLocId) !== locIdStr) continue;
        }
        hasAnyOpenFO = true;
        for (const lie of (fullFo.lineItems?.edges || [])) {
            const liNode = lie.node;
            const qty = liNode?.remainingQuantity || 0;
            const oli = liNode?.lineItem?.id;
            if (qty > 0 && oli) {
                unfulfilledLineItemIds.add(oli);
                remainingQtyByLi.set(oli, (remainingQtyByLi.get(oli) || 0) + qty);
            }
        }
    }
    // Reprint scoping: when there are no OPEN FOs at this location (all shipped),
    // the historical logic fell through to `rawItems` (every item on the order,
    // even from other locations). On a reprint from an emulated single location
    // that's wrong — the operator wants the paperwork for THEIR items only.
    // Collect line item IDs from ALL FOs (any status) at this location so we
    // can restrict rawItems to that set.
    const locScopedLineItemIds = new Set();
    if (locIdStr) {
        for (const fofE of (fullOrder?.fulfillmentOrders?.edges || [])) {
            const fullFo = fofE.node;
            const foLocId = (fullFo.assignedLocation?.location?.id || '').split('/').pop();
            if (String(foLocId) !== locIdStr) continue;
            for (const lie of (fullFo.lineItems?.edges || [])) {
                const oli = lie.node?.lineItem?.id;
                if (oli) locScopedLineItemIds.add(oli);
            }
        }
    }
    // 2026-09-25 (Justin, SURF-99896): "Reprint full order, not just
    // unfulfilled items" — a lost ticket needs every line at its ordered
    // quantity, whatever has since been fulfilled and wherever it sits.
    const inScopeItems = printAll
        ? rawItems
        : hasAnyOpenFO
        ? rawItems
            .filter(li => unfulfilledLineItemIds.has(li.id))
            // Replace the LI's ORDER quantity with the still-to-ship qty
            // so partial shipments (e.g. 1 of 2 already shipped) print as
            // "qty 1" not "qty 2". Uses remainingQuantity summed across
            // this loc's OPEN/IN_PROGRESS FOs. Fallback to li.quantity if
            // the map somehow doesn't have the entry (shouldn't happen).
            .map(li => {
                const rem = remainingQtyByLi.get(li.id);
                if (rem == null || rem === li.quantity) return li;
                return { ...li, quantity: rem };
            })
        : (locIdStr && locScopedLineItemIds.size > 0
            ? rawItems.filter(li => locScopedLineItemIds.has(li.id))
            : rawItems);

    // 2026-07-07: detect split-across-locations orders. Count distinct
    // assignedLocationIds among FOs that carry any REAL line item (any
    // status). If >1 the order has real work at multiple ops locs
    // (Teams+Teams, Warehouse+Warehouse, or Teams+Warehouse) — the
    // printout shows a per-loc subtotal reflecting only THIS loc's
    // slice and omits shipping / tax / order-total.
    //
    // Title on `fo.lineItems.edges[].node.lineItem` is often empty
    // (it's a reference; the real title lives on ORDER.lineItems). So
    // we build a set of NON-virtual parent-line-item IDs from the
    // order-level items and match by ID inside each FO.
    const _realLineItemIds = new Set(
        rawItems.filter(li => !isVirtualItem(li) && li?.id).map(li => li.id)
    );
    const _distinctFoLocIds = new Set();
    for (const fofE of (fullOrder?.fulfillmentOrders?.edges || [])) {
        const fullFo = fofE.node;
        const foItems = (fullFo.lineItems?.edges || []).map(e => e.node);
        const anyReal = foItems.some(li => {
            const pid = li?.lineItem?.id;
            const qty = Number(li?.totalQuantity || 0);
            return pid && qty > 0 && _realLineItemIds.has(pid);
        });
        if (!anyReal) continue;
        const foLocId = (fullFo.assignedLocation?.location?.id || '').split('/').pop();
        if (foLocId) _distinctFoLocIds.add(String(foLocId));
    }
    const isSplitAcrossLocations = _distinctFoLocIds.size > 1;

    // Per-loc subtotal (only the items being printed on this slice)
    const _perLocSubtotalSum = inScopeItems.reduce((s, li) => s + _liSubtotal(li), 0);
    const perLocSubtotal = _perLocSubtotalSum > 0
        ? _perLocSubtotalSum.toFixed(2)
        : subtotal;

    // Split virtual items out — they render at the bottom as one-liners, no image.
    const virtualItems = inScopeItems.filter(isVirtualItem);
    const realItems    = inScopeItems.filter(li => !isVirtualItem(li));

    const bundles = fullOrder
        ? groupLineItems(realItems, ord.tags || o.tags || [])
        : realItems.map(li => ({ item: li, children: [] }));

    const playerGroups = buildPlayerGroups(bundles);

    // "Guest User" is a Shopify-cart placeholder Team Portal writes when the
    // buyer didn't name a specific player (ordering for themselves). The
    // packer wants the actual customer identity on the printout, not a
    // generic placeholder. Mirror the same swap the backend does in
    // _build_slip_players / _build_packing_slip_items — prefer the shipping-
    // address name, fall back to the Shopify customer object.
    const customerName = (shipTo?.name || '').trim()
        || [ord.customer?.firstName, ord.customer?.lastName].filter(Boolean).join(' ').trim();
    if (customerName) {
        for (const grp of playerGroups) {
            if ((grp.name || '').trim().toLowerCase() === 'guest user') {
                grp.name = customerName;
            }
        }
    }

    // Team Portal number override — swap in the RDS number (Settings →
    // Team Portal). The printed slip must reflect the current authoritative
    // number so pressers/pickers see #7 on the slip, not the stale #38 that
    // was embedded in the order at checkout. Applied to PartyStrip (top
    // summary row), PlayerGroup headers, AND the per-bundle Number attr
    // rendered inside each jersey (via BundleItem printedNumber prop).
    //
    // Name lookup is primary (jerseys have Player Name in their line-item
    // attrs). Number-lookup fallback handles Stahls Number embellishment
    // groups whose attrs may lack a Player Name, but is skipped when two
    // players shared the same pre-edit number so we can't safely
    // disambiguate.
    const tpOverridesByName = ((fullOrder?.tp_number_overrides) || []).reduce((m, ov) => {
        if (ov && ov.player_name) m[ov.player_name] = ov;
        return m;
    }, {});
    const tpOverridesByOrderNumber = (() => {
        const seen = new Map();
        for (const ov of (fullOrder?.tp_number_overrides || [])) {
            if (!ov || !ov.order_number) continue;
            const k = String(ov.order_number);
            seen.set(k, seen.has(k) ? 'AMBIGUOUS' : ov);
        }
        const out = {};
        for (const [k, v] of seen.entries()) if (v !== 'AMBIGUOUS') out[k] = v;
        return out;
    })();
    for (const grp of playerGroups) {
        const nm = (grp.name || '').trim();
        const ov = (nm && tpOverridesByName[nm])
            || (grp.number != null && tpOverridesByOrderNumber[String(grp.number)])
            || null;
        if (ov && ov.override_number && String(ov.override_number) !== String(grp.number || '')) {
            grp.number = String(ov.override_number);
            grp._isTpOverride = true;
        }
    }

    // At Warehouse locations, reorder each player-group's bundles by the
    // parent line item's bin (bijective base-26 aisle order: A..Z, AA..ZZ,
    // AAA..). Picker walks the warehouse aisle in physical order on the
    // printout instead of Shopify-line-item order. No-op at non-Warehouse
    // (Teams) locations where bins aren't aisle-coded.
    //
    // Resolve the print's location name in this order:
    //   1. fo.location_name (pick-list path passes this directly)
    //   2. locationId → fullOrder.fulfillmentOrders[*].assignedLocation.name
    //      (single-order print path: OrderDetail/V2 pass locationId but
    //       order._location_name is null in some shapes — was breaking the
    //       sort when invoked from the Order Detail page).
    //   3. Any FO's assignedLocation.name matching /warehouse/i (all-locs
    //      admin print).
    let printLocationName = fo?.location_name || '';
    if (!printLocationName) {
        const foEdges = (fullOrder?.fulfillmentOrders?.edges || []);
        if (locIdStr) {
            const match = foEdges.find(e => {
                const id = (e.node?.assignedLocation?.location?.id || '').split('/').pop();
                return String(id) === locIdStr;
            });
            printLocationName = match?.node?.assignedLocation?.name || '';
        }
        if (!printLocationName) {
            const anyWarehouse = foEdges.find(e =>
                isWarehouseLocation(e.node?.assignedLocation?.name)
            );
            if (anyWarehouse) printLocationName = anyWarehouse.node.assignedLocation.name;
        }
    }
    if (isWarehouseLocation(printLocationName)) {
        const binFor = (b) => {
            const parent = (b && b.item) || null;
            const pid = parent?.id;
            return (pid && bins && bins[pid]) || '';
        };
        for (const grp of playerGroups) {
            if (!grp.bundles || grp.bundles.length < 2) continue;
            grp.bundles.sort((a, b) => compareBins(binFor(a), binFor(b)));
        }
    }

    // Club + team names live on order custom attributes for single-order
    // prints (the list-order `fo` carries them directly; the single-order
    // path doesn't). Falls back through:
    //   1. fo.order.club         — list-view metafield summary
    //   2. _soccer_club_name     — authoritative order attribute
    let club = o.club || null;
    const orderAttrs = ord.customAttributes || [];
    if (!club) {
        const cn = orderAttrs.find(a => a.key === '_soccer_club_name');
        if (cn && cn.value) club = cn.value;
    }
    // _soccer_order_info is the authoritative player→team/number map. Each
    // entry has { name, team: {id, title}, config: {number,...} }. We build
    // per-name maps so the PlayerGroup bar can fill in team AND number when
    // the line-item attrs didn't carry them (carts that omit Player Number
    // from line items still record it on the order-level attribute).
    const attrTeams = [];
    const playerTeamByName = {};
    const playerNumberByName = {};
    try {
        const raw = orderAttrs.find(a => a.key === '_soccer_order_info');
        if (raw && raw.value) {
            const arr = JSON.parse(raw.value);
            const seen = new Set();
            for (const p of (arr || [])) {
                const teamRaw = p?.team ?? p?.config?.team;
                const t = typeof teamRaw === 'string' ? teamRaw : teamRaw?.title;
                if (t && !seen.has(t)) { seen.add(t); attrTeams.push(t); }
                const nm = p?.name;
                if (nm && t) playerTeamByName[nm] = t;
                // Broken-order team-bulk: assigned_number array trumps singular config.number.
                const assigned = p?.config?.assigned_number;
                if (Array.isArray(assigned) && assigned.length > 0) {
                    const nums = assigned.map(n => String(n).trim()).filter(Boolean);
                    if (nm && nums.length) {
                        try { nums.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)); } catch (_) {}
                        playerNumberByName[nm] = nums.join(', ');
                        continue;
                    }
                }
                const num = p?.config?.number ?? p?.number;
                if (nm && num != null && String(num).trim() !== '') {
                    playerNumberByName[nm] = String(num).trim();
                }
            }
        }
    } catch (_) { /* malformed JSON → no teams/numbers */ }
    // Fill team + number onto each playerGroup that doesn't already have them.
    for (const g of playerGroups) {
        if (!g.team   && g.name && playerTeamByName[g.name])   g.team   = playerTeamByName[g.name];
        if (!g.number && g.name && playerNumberByName[g.name]) g.number = playerNumberByName[g.name];
    }
    // Club name can also surface from _soccer_order_info[].club.title when
    // _soccer_club_name isn't present.
    if (!club) {
        try {
            const raw = orderAttrs.find(a => a.key === '_soccer_order_info');
            if (raw && raw.value) {
                const arr = JSON.parse(raw.value);
                for (const p of (arr || [])) {
                    const c = p?.club;
                    if (c) {
                        club = typeof c === 'string' ? c : c?.title;
                        if (club) break;
                    }
                }
            }
        } catch (_) { /* ignore */ }
    }
    const locationName = fo?.location_name || '';

    // Total physical pieces — sum of bundle-parent / standalone-apparel
    // quantities. Skips bundles whose only resolvable parent is itself an
    // embellishment (e.g. a standalone Logo line item), since the count is
    // meant to reflect apparel/bundles, not the embellishments inside them.
    // Example: 2 bundles (jersey+logo+number) + 2 standalone socks → 4.
    const totalPieces = bundles.reduce((sum, b) => {
        const p = resolveParent(b);
        if (!p || EMBELLISHMENT_TYPES.has(p.productType)) return sum;
        return sum + (p?.quantity || 0);
    }, 0);
    // 2026-09-29 (Eric): "Total items · Line items" above the column header —
    // same rows the bottom TOTAL counts (garments only, embellishments ride
    // inside their bundle), so the two figures always agree.
    const lineCount = bundles.reduce((n, b) => {
        const p = resolveParent(b);
        return (!p || EMBELLISHMENT_TYPES.has(p.productType)) ? n : n + 1;
    }, 0);

    // Hide the Bin column entirely when the location has no picking-metafield
    // configured (bin-locations endpoint returned an empty {}). The Bin
    // header + each row's bin cell disappear, and the grid template shrinks
    // from 6 columns to 5 (via the .sp-doc--no-bin class below).
    const hasBinColumn = Object.keys(bins || {}).length > 0;

    const activeNotifications = ord?.active_notifications || [];
    const jumbledBundles      = ord?.jumbled_bundles || [];

    const fontFaceCss = buildFontFaceCss(numberPreviews);

    return (
        <SkuNotifContext.Provider value={skuNotifications || {}}>
        <article {...(hideHeader ? { 'data-order-block': 'true' } : { 'data-order-card': 'true' })} className={`sp-doc sp-doc--combined${hasBinColumn ? '' : ' sp-doc--no-bin'}${hideHeader ? ' sp-doc--block' : ''}`}>
            {fontFaceCss && <style>{fontFaceCss}</style>}
            {blockLabel && hideHeader && (
                <div className="sp-nj-block-label" style={{ borderTop: '3px solid #000', borderBottom: '1px solid #000', margin: '18px 0 8px', padding: '6px 0 4px', fontWeight: 700, fontSize: 13, letterSpacing: 0.6, textTransform: 'uppercase', pageBreakInside: 'avoid' }}>
                    {blockLabel}
                </div>
            )}
            {!hideHeader && <div data-order-header="true">
                <DocHeader
                    orderName={name}
                    originalName={replacementOriginal}
                    orderDate={fmtDate(createdAt)}
                    // 2026-07-07: hide the club on Warehouse-side prints
                    // (Warehouse fulfillment is base-garment picking; the
                    // club name belongs to the Teams-side pack that will
                    // press embellishments onto the item). Kept for
                    // Teams / non-warehouse locs.
                    club={isWarehouseLocation(printLocationName) ? null : club}
                    location={locationName}
                    storeKey={storeKey}
                    storeDisplayName={storeDisplayName}
                    page={1}
                    totalPages={1}
                />
                <PartyStrip
                    isWarehouse={isWarehouseLocation(printLocationName)}
                    club={isWarehouseLocation(printLocationName) ? null : club}
                    players={playerGroups
                        .filter(g => g.name)
                        .map(g => ({ name: g.name, number: g.number, team: g.team, initials: g.initials }))}
                    shipTo={pickupOverride ? null : shipTo}
                    deliveryMethod={deliveryMethodTitle}
                    pickupStoreName={pickupStoreName}
                    fulfillBy={fulfillByIso ? fmtDate(fulfillByIso) : null}
                    // When the override is active the shippingLine.title still
                    // reflects the customer's original checkout choice (e.g.
                    // "Soccer Post Leesburg"), but the operative pickup
                    // destination is the override. Replace the Method line
                    // so the printout matches the rest of the doc.
                    shippingLine={pickupOverride ? pickupOverride : ord.shippingLine?.title}
                    // B2B only (Eric, 2026-10-02): the order's note prints at the top.
                    orderNote={storeKey === 'b2b' ? ord.note : null}
                />
            </div>}

            {!hideHeader && Array.isArray(activeNotifications) && activeNotifications.length > 0 && (
              <div style={{
                border: '2px solid #000',
                borderRadius: 4,
                padding: '8px 10px',
                margin: '12px 0',
                pageBreakInside: 'avoid',
              }}>
                <div style={{
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  marginBottom: 6,
                  borderBottom: '1px solid #000',
                  paddingBottom: 4,
                }}>
                  Notifications
                </div>
                {activeNotifications.map((n, i) => (
                  <div
                    key={n.id ?? i}
                    style={{
                      fontSize: 12,
                      marginTop: i === 0 ? 0 : 6,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {n.message}
                  </div>
                ))}
              </div>
            )}

            {!hideHeader && Array.isArray(jumbledBundles) && jumbledBundles.length > 0 && (
              <div style={{
                border: '2px solid #000',
                borderRadius: 4,
                padding: '8px 10px',
                margin: '12px 0',
                pageBreakInside: 'avoid',
                background: '#fee2e2',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact',
              }}>
                <div style={{
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  marginBottom: 6,
                  borderBottom: '1px solid #000',
                  paddingBottom: 4,
                }}>
                  ⚠ Jumbled Bundle Alert
                </div>
                {jumbledBundles.map((jb, bi) => (
                  <div key={jb.group_id || bi}
                       style={{ marginTop: bi === 0 ? 0 : 8, fontSize: 12 }}>
                    <div style={{ fontWeight: 600 }}>
                      Bundle "{jb.label}"{jb.player_suffix ? ` ${jb.player_suffix}` : ''}
                    </div>
                    {(jb.fos || []).map((f, fi) => (
                      <div key={f.fo_id || fi}
                           style={{ marginTop: 3, marginLeft: 10 }}>
                        <div style={{ fontWeight: 600 }}>
                          {f.location_name || '(unknown location)'}{' '}
                          <span style={{ fontWeight: 400, color: '#333' }}>
                            ({(f.items || []).reduce((s, it) => s + (it.quantity || 0), 0)} item{
                              (f.items || []).reduce((s, it) => s + (it.quantity || 0), 0) === 1 ? '' : 's'
                            })
                          </span>
                        </div>
                        {(f.items || []).map((it, ii) => (
                          <div key={ii} style={{ marginLeft: 14, fontSize: 11 }}>
                            {it.quantity}× {it.title}
                            {it.sku ? (
                              <span style={{ color: '#555' }}> · sku: {it.sku}</span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div data-order-content="true">
                {blockLabel && !hideHeader && (
                    <div className="sp-nj-block-label" style={{ borderBottom: '1px solid #000', margin: '10px 0 8px', padding: '6px 0 4px', fontWeight: 700, fontSize: 13, letterSpacing: 0.6, textTransform: 'uppercase', pageBreakInside: 'avoid' }}>
                        {blockLabel}
                    </div>
                )}
                {totalPieces > 0 && (
                    <div className="sp-items-counts">
                        <span>Total items <b className="sp-tnum">{totalPieces}</b></span>
                        <span>Line items <b className="sp-tnum">{lineCount}</b></span>
                    </div>
                )}
                <div className="sp-items-thead">
                    <div />
                    <div>Description</div>
                    {hasBinColumn && <div>Bin</div>}
                    <div>Variant</div>
                    <div className="sp-items-thead__qty">Qty</div>
                    <div className="sp-items-thead__price">Price</div>
                </div>

                {playerGroups.map((grp) => (
                    <PlayerGroup
                        key={grp.key}
                        name={grp.name}
                        number={grp.number}
                        team={grp.team}
                        role={grp.role}
                        initials={grp.initials}
                    >
                        {grp.bundles.map((b, bi) => {
                            const parent = resolveParent(b);
                            const kids = resolveChildren(b, parent);
                            if (kids.length > 0) {
                                return (
                                    <BundleItem
                                        key={bi}
                                        parent={parent}
                                        children={kids}
                                        bins={bins}
                                        overstockBins={overstockBins}
                                        hasBinColumn={hasBinColumn}
                                        numberPreviews={numberPreviews}
                                        printedNumber={grp._isTpOverride ? grp.number : null}
                                    />
                                );
                            }
                            return (
                                <LineItem
                                    key={bi}
                                    item={parent}
                                    bin={bins[parent?.id]}
                                    overstock={overstockBins[parent?.id]}
                                    hasBinColumn={hasBinColumn}
                                />
                            );
                        })}
                    </PlayerGroup>
                ))}

                {virtualItems.length > 0 && (
                    <div className="sp-virtual-list">
                        {virtualItems.map((li) => {
                            const priceAmt = li.discountedUnitPriceSet?.shopMoney?.amount
                                ?? li.originalUnitPriceSet?.shopMoney?.amount
                                ?? li.variant?.price ?? li.price;
                            return (
                                <div key={li.id} className="sp-virtual-row">
                                    <span className="sp-virtual-row__title">{li.title}</span>
                                    {li.sku && (
                                        <span className="sp-virtual-row__sku sp-code">
                                            {li.sku}
                                            <SkuNotifBadge sku={li.sku} size="sm" />
                                        </span>
                                    )}
                                    <span className="sp-virtual-row__qty sp-tnum">{li.quantity ?? ''}</span>
                                    <span className="sp-virtual-row__price sp-tnum">
                                        {priceAmt != null ? `$${parseFloat(priceAmt).toFixed(2)}` : ''}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {totalPieces > 0 && (
                    <div className={`sp-items-total${hasBinColumn ? '' : ' sp-items-total--no-bin'}`}>
                        <div />
                        <div className="sp-items-total__label">Total</div>
                        {hasBinColumn && <div />}
                        <div />
                        <div className="sp-items-total__qty sp-tnum">{totalPieces}</div>
                        <div />
                    </div>
                )}

                <Totals
                    subtotal={isSplitAcrossLocations ? perLocSubtotal : subtotal}
                    shipping={isSplitAcrossLocations ? null : shipping}
                    shippingLabel={isSplitAcrossLocations ? null : (locationName || null)}
                    tax={isSplitAcrossLocations ? null : tax}
                    discount={isSplitAcrossLocations ? null : discount}
                    total={isSplitAcrossLocations ? null : total}
                />

                {!(blockLabel && !hideHeader) && (
                <footer
                    className="sp-doc-footer sp-doc-footer--receipt"
                    // Two-pass sanitization: backend allowlist on save +
                    // frontend allowlist pass via sanitizeFooterHtml (same
                    // tag/attr rules, DOMParser-based). Defense in depth so
                    // a future backend regression can't slip script/iframe
                    // /event-handler markup into the print popup.
                    dangerouslySetInnerHTML={{ __html: sanitizeFooterHtml(printoutFooterHtml || DEFAULT_FOOTER_HTML) }}
                />
                )}
            </div>
        </article>
        </SkuNotifContext.Provider>
    );
}

// ─── POPUP_SCRIPT ───────────────────────────────────────────────────────────
// Image-wait → set Page-N-of-N counter from content height → window.print().
// No explicit page-break injection — browsers handle breaking via the CSS
// rules (@page margin, break-inside: avoid on small atoms, break-inside:
// auto on bundles). This is what the user's "as little whitespace as
// possible" feedback called for: let a bundle's embellishment subs flow
// onto the next page rather than bumping the whole bundle forward.
/** One order, two NJ warehouse blocks (2026-09-26, Eric): the first block keeps
 *  the order header and lists the Tinton Falls items under a "Tinton Falls
 *  Warehouse" label; the second block drops the header and lists the Eatontown
 *  items under a heavy rule. Each block is a normal OrderPrintCard scoped to its
 *  own location, so bins, open quantities and the reprint-full rule all behave
 *  as they do for a single-location print. `locs` = [{ id, name, bins, overstockBins }]. */
export function MergedNjPrint({ fo, fullOrder, locs, numberPreviews = {}, storeKey = 'soccerpost', storeDisplayName = 'Soccer Post', printoutFooterHtml = null, skuNotifications = {}, printAll = false }) {
    const names = (locs || []).map(l => l.name).join(' + ');
    return (
        <div data-merged-nj="true">
            {(locs || []).map((l, i) => (
                <OrderPrintCard key={l.id}
                    fo={{ ...(fo || {}), location_name: names }}
                    fullOrder={fullOrder}
                    bins={l.bins || {}} overstockBins={l.overstockBins || {}}
                    numberPreviews={numberPreviews} skuNotifications={skuNotifications}
                    locationId={l.id} storeKey={storeKey} storeDisplayName={storeDisplayName}
                    printoutFooterHtml={printoutFooterHtml} printAll={printAll}
                    hideHeader={i > 0} blockLabel={l.name} />
            ))}
        </div>
    );
}

export const POPUP_SCRIPT = `(function(){
  var PAGE_H = 1040;
  function updatePageCounters() {
    // Approximate total pages from the body's rendered height. Good enough
    // for the header "Page 1 of N" display.
    var total = Math.max(1, Math.ceil(document.body.scrollHeight / PAGE_H));
    var spans = document.querySelectorAll('[data-total-pages]');
    for (var i = 0; i < spans.length; i++) spans[i].textContent = String(total);
    return total;
  }
  function installContinuationHeader(total) {
    if (total < 2) return;
    // Per-order header via NAMED PAGES — each order article gets its own
    // page name, and each named-page @page rule hardcodes that order's
    // name in the top-right content. Chrome reliably supports named pages
    // + literal content + counter(page) + counter(pages); it does NOT
    // reliably support string() in @page top-right (silent empty render
    // breaks the whole content expression).
    //
    // counter(page) / counter(pages) stay global because Chrome forbids
    // counter-reset on the page counter (CSS Paged Media spec). So pages
    // read e.g. "SP-247789 · Page 4 of 11" — order name is correct
    // per page, page counter is global doc-wide.
    var orders = Array.from(document.querySelectorAll('[data-order-card="true"]'));
    // Remove the .print-page-break separator divs between orders. Switching
    // named pages via 'page: ord_K' already forces a page break between
    // adjacent named-page boxes; the explicit separator on top of that
    // produced a blank page between every pair of orders.
    var seps = document.querySelectorAll('.print-page-break');
    for (var s = 0; s < seps.length; s++) seps[s].remove();
    var rules = [];
    orders.forEach(function(node, oIdx) {
      var hero = node.querySelector('.sp-h-hero');
      if (!hero) return;
      var orderName = (hero.textContent || '').replace(/\\\\/g, '\\\\\\\\').replace(/"/g, '\\\\"');
      var k = oIdx + 1;
      node.classList.add('sp-print-order-' + k);
      rules.push('.sp-print-order-' + k + ' { page: ord_' + k + '; }');
      rules.push('@page ord_' + k + ' {');
      rules.push('  @top-right {');
      rules.push('    content: "' + orderName + '";');
      rules.push('    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;');
      rules.push('    font-size: 10px;');
      rules.push('    font-weight: 600;');
      rules.push('    color: #6d7175;');
      rules.push('    letter-spacing: 0.06em;');
      rules.push('    text-transform: uppercase;');
      rules.push('  }');
      rules.push('}');
    });
    var style = document.createElement('style');
    style.setAttribute('data-print-header','');
    style.appendChild(document.createTextNode(rules.join('\\n')));
    document.head.appendChild(style);
  }
  function go() {
    var total = updatePageCounters();
    installContinuationHeader(total);
    window.focus(); window.print();
  }
  function waitImages() {
    var imgs = Array.from(document.images);
    if (imgs.length === 0) { go(); return; }
    var remaining = imgs.length;
    var done = false;
    function tick() { if (--remaining <= 0 && !done) { done = true; go(); } }
    imgs.forEach(function(img) {
      if (img.complete) { tick(); }
      else {
        img.addEventListener('load', tick, { once: true });
        img.addEventListener('error', tick, { once: true });
      }
    });
    // 15s safety. With server-side prewarm the typical wait is ~100ms (browser
    // pulls from cache); raised from 2s so 50+ order bulk prints don't fire
    // window.print() while half the thumbnails are still loading.
    setTimeout(function() { if (!done) { done = true; go(); } }, 15000);
  }
  if (document.readyState === 'complete') waitImages();
  else window.addEventListener('load', waitImages);
})();`;

// ─── buildPopupHTML ─────────────────────────────────────────────────────────
export function buildPopupHTML(content) {
    const BLOCK_OPEN = '<' + 'script>';
    const BLOCK_CLOSE = '<' + '/script>';
    // Lock the popup's baseURI to the opener's origin so relative URLs like
    // /api/store-logos/<sk>.png resolve correctly. Browsers vary on whether a
    // window.open('')/document.write document inherits the opener's baseURI;
    // an explicit <base> removes the ambiguity.
    const baseHref = (typeof window !== 'undefined' && window.location && window.location.origin)
        ? window.location.origin + '/'
        : '/';
    return `<!DOCTYPE html><html>
<head><meta charset="utf-8"><title>Soccer Post Orders</title>
<base href="${baseHref}">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700&display=swap">
<style>
:root {
  --sp-ink:#111418; --sp-paper:#fff; --sp-ink-soft:#374151;
  --sp-muted:#6d7175; --sp-muted-2:#9ca3af;
  --sp-rule:#d0d5dd; --sp-rule-2:#e5e7eb; --sp-band:#f3f4f6;
  --sp-highlight:#fff7cc; --sp-highlight-ink:#6b4e00;
  --sp-ok:#0f8a4a; --sp-danger:#c8102e; --sp-info:#1a4fa0; --sp-info-bg:#dce8f7;
  --sp-font-sans:-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  --sp-font-mono:ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
  --sp-font-display:"Barlow Condensed", "IBM Plex Sans Condensed", -apple-system, BlinkMacSystemFont, sans-serif;
  --sp-rule-hair:1px solid var(--sp-rule);
  --sp-rule-soft:1px solid var(--sp-rule-2);
  --sp-rule-strong:2px solid var(--sp-ink);
}
html, body { margin:0; padding:0; background:#fff; color:var(--sp-ink); }
body { font-family: var(--sp-font-sans); font-size:12px; line-height:1.45; max-width: 7.3in; margin: 0 auto; padding: 0.3in 0.4in; }
* { -webkit-print-color-adjust:exact; print-color-adjust:exact; box-sizing:border-box; }
img { display:block; }
@media print {
  @page { margin: 0.4in 0.5in; }
  body { max-width: none; margin: 0; padding: 0; }
}

.sp-doc { font-family: var(--sp-font-sans); color: var(--sp-ink); font-variant-numeric: tabular-nums; }
.sp-doc * { box-sizing: border-box; }

.sp-h-hero { font-family: var(--sp-font-display); font-weight: 700; font-size: 32px; line-height: 1.15; letter-spacing: -0.01em; text-transform: uppercase; color: var(--sp-ink); font-variant-numeric: tabular-nums; }
.sp-h-section { font-weight: 700; font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--sp-muted); line-height: 1; }
.sp-p { font-size: 12px; line-height: 1.45; color: var(--sp-ink); }
.sp-p-muted { font-size: 11px; color: var(--sp-muted); }
.sp-code { font-family: var(--sp-font-mono); font-size: 10px; color: var(--sp-muted); }
.sp-tnum { font-variant-numeric: tabular-nums; }
.sp-right { text-align: right; }

.sp-badge-num { display: inline-block; padding: 1px 7px; border-radius: 9999px; background: var(--sp-info-bg); color: var(--sp-info); font-size: 11px; font-weight: 700; font-variant-numeric: tabular-nums; }
/* Initials/text-to-apply badge — light purple on yellow so packers visually
   confirm the customization text (e.g. SKA on a Stahls silicone-initials line)
   without scanning the items table. Sits to the right of the #number badge. */
.sp-badge-ini { display: inline-block; padding: 1px 7px; border-radius: 9999px; background: #fef08a; color: #6b21a8; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* Team badge on the per-player row — violet pill so it contrasts with the
   blue #number badge sitting right next to it. */
.sp-badge-team { display: inline-block; padding: 1px 9px; border-radius: 9999px; background: #ede9fe; color: #4c1d95; font-size: 11px; font-weight: 600; letter-spacing: 0.03em; }
/* Single neutral pill style — same for Returnable, Not returnable,
   Exchangeable. Fully rounded corners, 20% smaller than before. */
.sp-pill { display: inline-block; padding: 1px 8px; font-size: 8px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; border-radius: 9999px; background: var(--sp-band); color: var(--sp-ink-soft); }
.sp-highlight { background: var(--sp-highlight); color: var(--sp-highlight-ink); padding: 1px 4px; border-radius: 2px; }

/* Header: larger logo, middle column centered horizontally + vertically
   between logo and barcode. */
.sp-doc-header { display: grid; grid-template-columns: 190px minmax(0, 1fr) auto; gap: 14px; align-items: center; padding-bottom: 10px; border-bottom: var(--sp-rule-strong); }
.sp-doc-header__left { display: flex; align-items: center; }
.sp-doc-header__logo { width: 100%; max-width: 190px; height: auto; display: block; object-fit: contain; }
.sp-doc-header__mid { text-align: center; }
.sp-doc-header__title-row { display: flex; align-items: baseline; gap: 10px; line-height: 1; justify-content: center; }
.sp-doc-header__meta { display: flex; flex-wrap: wrap; gap: 4px 20px; margin: 8px 0 0; padding: 0; justify-content: center; }
.sp-doc-header__meta > div { display: flex; gap: 6px; align-items: baseline; white-space: nowrap; margin: 0; }
.sp-doc-header__meta dt { font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--sp-muted); margin: 0; font-weight: 600; }
.sp-doc-header__meta dd { margin: 0; font-size: 11px; font-weight: 600; color: var(--sp-ink); }
.sp-doc-header__right { justify-self: end; padding-left: 10px; max-width: 250px; }
.sp-doc-header__right svg { max-width: 100%; height: auto; display: block; }
.sp-doc-header__title-row .sp-h-hero { white-space: nowrap; }

/* Left column (Order: club + players + teams) gets the widest share,
   Ship-To is also wide, Method is narrow — its content is short
   ("Team Portal Flat Rate Shipping") but tight is fine. */
.sp-party { display: grid; grid-template-columns: 1.55fr 1.35fr 0.6fr; gap: 18px; padding: 10px 0 12px; border-bottom: var(--sp-rule-hair); }
/* Keep the visual separation between Order and Ship-To — smaller nudge
   now because Order column is inherently wider. */
.sp-party > :nth-child(2) { margin-left: 80px; }
.sp-party__col .sp-h-section { margin-bottom: 4px; }
/* Club name in the Order column — matches the player-name size (20px).
   The Ship-To name and Method name reuse the same class so both would
   otherwise get the same 20px; override below shrinks them to 12px (body
   size, same as the address lines) while keeping them bold. */
.sp-party__name { font-size: 20px; font-weight: 700; color: var(--sp-ink); line-height: 1.25; margin-bottom: 2px; }
.sp-party > :nth-child(2) .sp-party__name,
.sp-party > :nth-child(3) .sp-party__name {
    font-size: 12px;
    line-height: 1.35;
}
/* Player list: each player-row pairs name+#number with their team on the
   line below, so the stack reads player → team → player → team rather
   than clustering all players then all teams. */
.sp-party__players { margin-top: 6px; }
.sp-party__player-row { margin-bottom: 8px; }
.sp-party__player-row:last-child { margin-bottom: 0; }
.sp-party__player { display: flex; align-items: baseline; gap: 9px; font-size: 20px; color: var(--sp-ink); line-height: 1.25; }
.sp-party__player-name { font-weight: 700; }
.sp-party__player .sp-badge-num { font-size: 17px; padding: 2px 9px; }
.sp-party__player .sp-badge-ini { font-size: 17px; padding: 2px 9px; margin-left: 4px; }
.sp-party__player-team { font-size: 13px; color: var(--sp-ink-soft); font-weight: 600; line-height: 1.3; margin-top: 1px; }

/* Description · Bin · Variant · Qty · Price bar — styled to match the
   PlayerGroup bar (user's request). Light band, dark ink, hair rules. */
.sp-items-counts { display: flex; gap: 18px; justify-content: flex-end; margin-top: 12px; padding: 0 8px; font-size: 11px; color: var(--sp-muted); text-transform: uppercase; letter-spacing: 0.06em; }
.sp-items-counts b { color: var(--sp-ink); font-size: 13px; font-weight: 700; margin-left: 4px; }
.sp-items-counts + .sp-items-thead { margin-top: 4px; }
.sp-items-thead { display: grid; align-items: center; padding: 4px 8px; background: var(--sp-band); color: var(--sp-ink); margin-top: 12px; border-top: var(--sp-rule-hair); border-bottom: var(--sp-rule-hair); }
.sp-items-thead > div { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--sp-muted); }
.sp-items-thead__qty, .sp-items-thead__price { text-align: right; }

.sp-items-thead, .sp-line, .sp-bundle {
  grid-template-columns: 130px minmax(0, 1fr) 90px 90px 40px 62px;
  column-gap: 10px;
}
/* No-bin mode: drop the 90px Bin column — header + rows shrink to 5 cols. */
.sp-doc--no-bin .sp-items-thead,
.sp-doc--no-bin .sp-line,
.sp-doc--no-bin .sp-bundle {
  grid-template-columns: 130px minmax(0, 1fr) 90px 40px 62px;
}

.sp-player-group { margin-top: 4px; }
.sp-player-group__bar { display: flex; align-items: center; gap: 10px; padding: 4px 8px; background: var(--sp-band); border-top: var(--sp-rule-hair); border-bottom: var(--sp-rule-hair); margin-top: 6px; }
.sp-player-group__name { font-size: 12px; font-weight: 700; color: var(--sp-ink); }
.sp-player-group__meta { font-size: 11px; color: var(--sp-muted); }
.sp-player-group__meta--faint { color: var(--sp-muted-2); }

.sp-line { display: grid; align-items: start; padding: 5px 8px; border-bottom: var(--sp-rule-soft); }
.sp-line__thumb { width: 69px; height: 69px; background: var(--sp-paper); border: 1px solid var(--sp-rule-2); display: flex; align-items: center; justify-content: center; overflow: hidden; }
.sp-line__thumb img { width: 100%; height: 100%; object-fit: contain; }
.sp-line__thumb-fallback { color: var(--sp-muted-2); font-size: 28px; }
.sp-line__thumb-fig { margin: 0; }
/* Single-product image: center inside the 130px preview column. Both
   justify-self (grid-axis placement) AND inner flex centering are set so
   it works whether the figure auto-stretches the cell or not. */
.sp-line__thumb-fig--single { justify-self: center; display: flex; justify-content: center; align-items: center; width: 100%; }
/* Single-preview bundle: same centering treatment as a single-item line. */
.sp-bundle__preview--single { justify-self: center; width: 100%; justify-content: center; }
.sp-line__body { min-width: 0; }
.sp-line__title { font-size: 12px; font-weight: 600; line-height: 1.3; color: var(--sp-ink); }
.sp-line__attrs { display: flex; flex-wrap: wrap; gap: 4px 10px; margin-top: 3px; }
.sp-line__attr { font-size: 10px; color: var(--sp-muted); }
.sp-line__attr-k { color: var(--sp-ink-soft); font-weight: 600; }
.sp-line__returnable { margin-top: 4px; }
.sp-line__bin, .sp-line__variant, .sp-line__qty, .sp-line__price { align-self: center; }
.sp-line__qty { text-align: right; font-size: 18px; font-weight: 700; }
.sp-line__variant { font-size: 18px; font-weight: 700; color: var(--sp-ink); }
.sp-line__price { text-align: right; font-size: 12px; font-weight: 600; }
.sp-line__price { font-weight: 700; }

/* Allow a bundle to split across pages (parent row on page 1, embellishment
   subs on page 2) so we don't waste bottom-of-page whitespace. Lines are
   short enough to keep atomic. */
.sp-bundle { display: grid; align-items: start; padding: 5px 8px; border-bottom: var(--sp-rule-soft); }
.sp-line   { break-inside: avoid; page-break-inside: avoid; }
/* The subs grid (embellishments) can itself split between tiles if the
   bundle happens to land right at a page edge. */
.sp-bundle__subs { break-inside: auto; }
.sp-bundle__preview { display: flex; gap: 6px; padding: 0; }
.sp-bundle__preview--single { justify-content: center; align-items: center; }
.sp-bundle__preview-fig { margin: 0; flex: 0 0 69px; display: flex; flex-direction: column; align-items: stretch; gap: 2px; }
.sp-bundle__preview-img { width: 69px; height: 69px; border: 1px solid var(--sp-rule-2); background: var(--sp-paper); display: flex; align-items: center; justify-content: center; overflow: hidden; }
.sp-bundle__preview-img img { width: 100%; height: 100%; object-fit: contain; }
.sp-bundle__preview-img--empty { color: var(--sp-muted-2); font-size: 26px; }
.sp-bundle__preview-fig figcaption { font-size: 10px; font-weight: 700; text-align: center; color: var(--sp-ink); }
.sp-bundle__body { min-width: 0; }
.sp-bundle__title-row { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.sp-bundle__title { font-size: 12px; font-weight: 700; color: var(--sp-ink); line-height: 1.25; }
.sp-bundle__sku-line { margin-top: 2px; font-size: 11px; }
.sp-bundle__attrs { display: flex; flex-wrap: wrap; gap: 4px 10px; margin-top: 3px; }
.sp-bundle__attr { font-size: 10px; color: var(--sp-muted); display: inline-flex; gap: 4px; align-items: baseline; }
.sp-bundle__attr-k { color: var(--sp-ink-soft); font-weight: 600; }
.sp-bundle__attr-v { color: var(--sp-ink); font-weight: 700; font-variant-numeric: tabular-nums; }
.sp-bundle__returnable { margin-top: 6px; }
.sp-bundle__bin, .sp-bundle__variant, .sp-bundle__qty, .sp-bundle__price { align-self: center; }
.sp-bundle__variant { font-size: 18px; font-weight: 700; color: var(--sp-ink); }
.sp-bundle__price { text-align: right; font-size: 12px; font-weight: 700; }
.sp-bundle__qty { text-align: right; font-size: 18px; font-weight: 700; }

/* Items table sum-line (apparel/bundles only; embellishments excluded). Mirrors
   the items grid so the count lands directly below the Qty column. */
.sp-items-total { display: grid; grid-template-columns: 130px minmax(0, 1fr) 90px 90px 40px 62px;
                   column-gap: 10px; padding: 8px 0 6px;
                   border-top: var(--sp-rule-strong); }
.sp-items-total--no-bin { grid-template-columns: 130px minmax(0, 1fr) 90px 40px 62px; }
.sp-items-total__label { font-size: 13px; font-weight: 700; color: var(--sp-ink);
                          letter-spacing: 0.04em; text-transform: uppercase; }
.sp-items-total__qty { text-align: right; font-size: 22px; font-weight: 800;
                        color: var(--sp-ink); letter-spacing: -0.01em; }

.sp-bundle__subs { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 16px; margin-top: 10px; }
.sp-bundle__sub { display: grid; grid-template-columns: 48px 1fr; gap: 8px; align-items: start; }
.sp-bundle__sub-thumb { width: 48px; height: 48px; border: 1px solid var(--sp-rule-2); background: var(--sp-paper); display: flex; align-items: center; justify-content: center; overflow: hidden; }
.sp-bundle__sub-thumb img { width: 100%; height: 100%; object-fit: contain; }
.sp-bundle__sub-thumb-label { font-size: 9px; font-weight: 700; color: var(--sp-ink); }
.sp-bundle__sub-body { min-width: 0; display: flex; flex-direction: column; gap: 1px; padding-top: 1px; }
.sp-bundle__sub-title { font-size: 11px; font-weight: 600; color: var(--sp-ink); line-height: 1.25; }
.sp-bundle__sub-sku { font-size: 10px; }
.sp-bundle__sub-bin { font-size: 12px; font-weight: 700; margin-top: 1px; text-align: left; }
.sp-bundle__sub-bin .sp-highlight { font-size: 12px; font-weight: 700; }

/* Bin column: primary on top (bold), overstock just below (same size, lighter).
   Both 12px, both left-aligned within the column. */
.sp-line__bin, .sp-bundle__bin { text-align: left; }
.sp-line__bin .sp-highlight,
.sp-bundle__bin .sp-highlight { font-size: 12px; font-weight: 700; padding: 2px 5px; }
.sp-line__overstock,
.sp-bundle__overstock { font-size: 12px; line-height: 1.25; margin-top: 3px; text-align: left; color: var(--sp-ink-2, #6b6b6b); }
.sp-line__overstock .sp-code,
.sp-bundle__overstock .sp-code { font-size: 12px; }
.sp-bundle__sub-overstock { font-size: 12px; line-height: 1.2; margin-top: 1px; text-align: left; color: var(--sp-ink-2, #6b6b6b); }
.sp-bundle__sub-overstock .sp-code { font-size: 12px; }

.sp-doc-pickstats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 10px; padding: 8px 0; border-top: var(--sp-rule-strong); border-bottom: var(--sp-rule-hair); }
.sp-doc-pickstats > div { display: flex; flex-direction: column; gap: 4px; }
.sp-doc-pickstats__num { font-size: 24px; font-weight: 700; color: var(--sp-ink); line-height: 1; }

.sp-totals { margin-left: auto; margin-top: 10px; width: 280px; display: flex; flex-direction: column; }
.sp-totals__row { display: flex; justify-content: space-between; align-items: baseline; padding: 3px 8px; font-size: 12px; }
.sp-totals__row--grand { margin-top: 4px; padding: 7px 8px 3px; border-top: var(--sp-rule-strong); font-size: 14px; font-weight: 700; }
.sp-totals__row--grand .sp-totals__value { font-size: 16px; font-weight: 700; }
.sp-totals__label { color: var(--sp-muted); }
.sp-totals__row--grand .sp-totals__label { color: var(--sp-ink); }
.sp-totals__value { color: var(--sp-ink); font-weight: 600; }

.sp-doc-footer { margin-top: 18px; padding-top: 12px; border-top: var(--sp-rule-hair); }
.sp-doc-footer--receipt { text-align: center; padding: 16px 0 6px; }
.sp-doc-footer__title { font-size: 14px; font-weight: 700; color: var(--sp-ink); }
.sp-doc-footer__sub { font-size: 11px; color: var(--sp-muted); margin-top: 2px; }

.sp-doc-header--cont { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 2px solid var(--sp-ink); margin-bottom: 12px; }

/* Compact virtual/service items row — one line, no image. Used for
   processing fees, returns protection, tips, etc. Always at the bottom. */
.sp-virtual-list { margin-top: 10px; border-top: var(--sp-rule-hair); }
.sp-virtual-row { display: grid; grid-template-columns: minmax(0,1fr) auto 40px 62px; column-gap: 10px; padding: 3px 8px; font-size: 10px; color: var(--sp-muted); border-bottom: var(--sp-rule-soft); align-items: baseline; }
.sp-virtual-row:last-child { border-bottom: none; }
.sp-virtual-row__title { color: var(--sp-ink-soft); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sp-virtual-row__sku { font-size: 9px; }
.sp-virtual-row__qty { text-align: right; font-weight: 600; color: var(--sp-ink-soft); }
.sp-virtual-row__price { text-align: right; font-weight: 700; color: var(--sp-ink); }

.print-page-break { page-break-after: always; break-after: page; }
</style>
</head><body>${content}${BLOCK_OPEN}${POPUP_SCRIPT}${BLOCK_CLOSE}</body></html>`;
}

export default OrderPrintCard;
